import { useEffect, useMemo, useState } from 'react';
import { collection, doc, limit, onSnapshot, query, Timestamp, where, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase.js';
import logger from './logger';

/**
 * REQ-02 — in-app moderation notifications for the signed-in user.
 * Notifications are written by Cloud Functions when resources are uploaded
 * into the user's scopes (or into unassigned scopes, for administrators).
 *
 * @param {string|null} userId subscribe only when set
 */
export default function useNotifications(userId) {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      return undefined;
    }
    const q = query(collection(db, 'notifications'), where('userId', '==', userId), limit(100));
    return onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
        setNotifications(list);
      },
      (err) => logger.error('Error loading notifications:', err)
    );
  }, [userId]);

  const unread = useMemo(() => notifications.filter((n) => !n.read), [notifications]);

  const markAllRead = async () => {
    if (unread.length === 0) return;
    try {
      const batch = writeBatch(db);
      unread.slice(0, 450).forEach((n) =>
        batch.update(doc(db, 'notifications', n.id), { read: true, readAt: Timestamp.now() })
      );
      await batch.commit();
    } catch (err) {
      logger.error('Error marking notifications read:', err);
    }
  };

  return { notifications, unreadCount: unread.length, markAllRead };
}
